import type { SectionKit } from './kit';

/** Creator / founder bio: a portrait beside the story, credentials as a check
 *  list, an optional row of stats, and a sign-off line. Image first in the DOM
 *  (phones: photo on top); `image_side` moves it on desktop. */
export default function BioBlock({ k }: { k: SectionKit }) {
  const { section, s, brand, tz, txt, hl, eyebrowCls, stack, ph } = k;
  const items = (section.items ?? []).map((b, i) => ({ b, i })).filter(({ b }) => Boolean(b));
  const stats = (section.stats ?? []).filter((st) => st.value || st.label);
  if (!section.photo_url && !section.heading && !section.body) return ph('Bio', 'Add a photo and your story in the panel');
  if (section.variant === 'compact') {
    // A byline: a small circle photo beside the name and one line (the
    // "from the desk of" opener of a long letter).
    return (
      <div className="mx-auto flex max-w-3xl items-center gap-5 sm:gap-6" style={k.contentStyle}>
        {section.photo_url && <img src={section.photo_url} alt="" loading="lazy" decoding="async" className="h-20 w-20 flex-none rounded-full object-cover sm:h-24 sm:w-24" />}
        <div className="grid min-w-0 gap-1">
          {section.eyebrow && <span className={`justify-self-start ${tz('eyebrow')} ${eyebrowCls}`}>{txt(section.eyebrow, (t) => ({ eyebrow: t }))}</span>}
          {section.heading && <h2 className={`text-xl sm:text-2xl ${s.heading}`}>{hl(section.heading, (t) => ({ heading: t }))}</h2>}
          {section.body && <p className={`${tz('small')} ${s.sub} whitespace-pre-line`}>{txt(section.body, (t) => ({ body: t }), true)}</p>}
        </div>
      </div>
    );
  }
  const right = section.image_side === 'right';
  const two = !stack;
  const photo = section.photo_url
    ? <img src={section.photo_url} alt="" loading="lazy" decoding="async" className={`aspect-[4/5] w-full object-cover ${s.media ?? 'rounded-3xl shadow-lg'}`} />
    : null;
  return (
    <div className={`mx-auto grid max-w-5xl items-center gap-8 ${two ? 'md:grid-cols-[minmax(0,5fr)_minmax(0,7fr)] md:gap-14' : ''}`} style={k.contentStyle}>
      <div className={two && right ? 'md:order-2' : ''}>{photo}</div>
      <div className={`grid content-center gap-4 ${two && right ? 'md:order-1' : ''}`}>
        {section.eyebrow && <span className={`justify-self-start ${tz('eyebrow')} ${eyebrowCls}`}>{txt(section.eyebrow, (t) => ({ eyebrow: t }))}</span>}
        {section.heading && <h2 className={`${tz('heading')} ${s.heading}`}>{hl(section.heading, (t) => ({ heading: t }))}</h2>}
        {section.body && <p className={`${tz('body')} ${s.sub} whitespace-pre-line`}>{txt(section.body, (t) => ({ body: t }), true)}</p>}
        {items.length > 0 && (
          <ul className="grid gap-2.5">
            {items.map(({ b, i }) => (
              <li key={i} className={`flex items-start gap-2.5 ${tz('body')}`}>
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={brand} strokeWidth="2.25" strokeLinecap="round" strokeLinejoin="round" className="mt-[3px] flex-none" aria-hidden><path d="M20 6 9 17l-5-5" /></svg>
                <span>{txt(b, (t) => { const a = [...(section.items ?? [])]; a[i] = t; return { items: a }; })}</span>
              </li>
            ))}
          </ul>
        )}
        {stats.length > 0 && (
          <div className={`mt-2 flex flex-wrap gap-x-10 gap-y-4 border-t pt-5 ${s.divider}`}>
            {stats.map((st, i) => (
              <div key={i}>
                <div className={`text-2xl font-extrabold leading-none tracking-tight sm:text-3xl ${s.heading}`}>{st.value}</div>
                <div className={`mt-1.5 text-sm ${s.label ?? s.sub}`}>{st.label}</div>
              </div>
            ))}
          </div>
        )}
        {(section.attribution || section.role) && (
          <div className="pt-1 text-sm">
            {section.attribution && <span className={`block font-semibold ${s.heading}`}>{txt(section.attribution, (t) => ({ attribution: t }))}</span>}
            {section.role && <span className={`block ${s.label ?? s.sub}`}>{txt(section.role, (t) => ({ role: t }))}</span>}
          </div>
        )}
      </div>
    </div>
  );
}
